import React from 'react';
import Icon from '../../../components/AppIcon';

const STATUS_COLORS = {
  idle: { dot: 'bg-success', text: 'text-success', badge: 'bg-success-100 text-success' },
  online: { dot: 'bg-success', text: 'text-success', badge: 'bg-success-100 text-success' },
  active: { dot: 'bg-blue-500', text: 'text-blue-500', badge: 'bg-blue-50 text-blue-600' },
  en_route: { dot: 'bg-warning', text: 'text-warning', badge: 'bg-warning-100 text-warning' },
  offline: { dot: 'bg-gray-400', text: 'text-gray-400', badge: 'bg-gray-100 text-gray-500' },
};

const DriverCard = ({ driver, onSelect, isSelected }) => {
  const statusCfg = STATUS_COLORS?.[driver?.status] || STATUS_COLORS?.offline;
  const isOnline = driver?.status !== 'offline';

  const formatLastSeen = (ts) => {
    if (!ts) return 'Unknown';
    const diff = Date.now() - new Date(ts)?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ago`;
  };

  return (
    <div
      onClick={() => onSelect?.(driver)}
      className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'border-accent bg-accent-50 shadow-md'
          : 'border-border bg-surface hover:border-primary/30 hover:bg-surface-hover'
      }`}
    >
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <span className="text-white text-sm font-bold">
              {driver?.name?.charAt(0)?.toUpperCase() || 'D'}
            </span>
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${statusCfg?.dot}`} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-text-primary truncate">{driver?.name}</p>
            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ml-1 ${statusCfg?.badge}`}>
              {driver?.status === 'en_route' ? 'En Route' : driver?.status?.charAt(0)?.toUpperCase() + driver?.status?.slice(1)}
            </span>
          </div>

          <div className="flex items-center space-x-2 mt-0.5">
            <Icon name="Car" size={11} className="text-text-muted" />
            <span className="text-xs text-text-muted truncate">{driver?.vehicleType} • {driver?.licensePlate}</span>
          </div>

          {/* Stats row */}
          <div className="flex items-center space-x-3 mt-1.5">
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={11} className="text-accent-500" />
              <span className="text-xs text-text-secondary">{parseFloat(driver?.rating || 0)?.toFixed(1)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Navigation" size={11} className="text-text-muted" />
              <span className="text-xs text-text-muted truncate max-w-24">{driver?.currentLocation || 'CBD'}</span>
            </div>
            {isOnline && (
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={11} className="text-text-muted" />
                <span className="text-xs text-text-muted">{formatLastSeen(driver?.lastActivity)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DriverAvailabilityPanel = ({ drivers = [], onDriverSelect, selectedDriver }) => {
  const online = drivers?.filter(d => d?.status !== 'offline');
  const idle = drivers?.filter(d => d?.status === 'idle' || d?.status === 'online');
  const active = drivers?.filter(d => d?.status === 'active' || d?.status === 'en_route');

  return (
    <div className="flex flex-col h-full bg-surface rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-primary border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={18} color="#ffd700" />
            <span className="text-sm font-semibold text-white">Driver Availability</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-white/80 text-xs">{online?.length} online</span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        <div className="px-3 py-2 text-center">
          <p className="text-lg font-bold text-success">{idle?.length}</p>
          <p className="text-xs text-text-muted">Available</p>
        </div>
        <div className="px-3 py-2 text-center">
          <p className="text-lg font-bold text-blue-500">{active?.length}</p>
          <p className="text-xs text-text-muted">On Trip</p>
        </div>
        <div className="px-3 py-2 text-center">
          <p className="text-lg font-bold text-text-muted">{drivers?.length - online?.length}</p>
          <p className="text-xs text-text-muted">Offline</p>
        </div>
      </div>

      {/* Driver List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {/* Available first */}
        {idle?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2 px-1">Available</p>
            <div className="space-y-2">
              {idle?.map(driver => (
                <DriverCard
                  key={driver?.id}
                  driver={driver}
                  onSelect={onDriverSelect}
                  isSelected={selectedDriver?.id === driver?.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* Active */}
        {active?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2 px-1 mt-3">On Trip</p>
            <div className="space-y-2">
              {active?.map(driver => (
                <DriverCard
                  key={driver?.id}
                  driver={driver}
                  onSelect={onDriverSelect}
                  isSelected={selectedDriver?.id === driver?.id}
                />
              ))}
            </div>
          </div>
        )}

        {drivers?.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <Icon name="UserX" size={32} className="text-text-muted mb-2" />
            <p className="text-sm text-text-muted">No drivers found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverAvailabilityPanel;
